/*
 * Pakketonderzoek — verklaring van niet-ontvangst (DPD) / antwoordformulier (PostNL)
 * Vult de officiële vervoerder-PDF's client-side in met pdf-lib.
 * Templates worden pas opgehaald bij het genereren (assets/dpd-verklaring-template.pdf
 * en assets/postnl-antwoordformulier.pdf, URL's via data-attributen op #pakketonderzoek).
 */
(function () {
  'use strict';

  const root = document.getElementById('pakketonderzoek');
  if (!root) return;

  const TEMPLATE_URLS = {
    dpd: root.dataset.dpdTemplate,
    postnl: root.dataset.postnlTemplate,
    dhl: root.dataset.dhlTemplate,
  };

  const templateCache = {};
  async function fetchTemplate(url) {
    if (!templateCache[url]) {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Het formulier kon niet geladen worden (' + res.status + '). Probeer het opnieuw.');
      templateCache[url] = await res.arrayBuffer();
    }
    return templateCache[url];
  }

  function pdfLib() {
    if (!window.PDFLib) throw new Error('De PDF-bibliotheek is niet geladen. Herlaad de pagina en probeer het opnieuw.');
    return window.PDFLib;
  }

  // ---- carrier switching ----
  const carrierBtns = root.querySelectorAll('.po-carrier-btn');
  const forms = {
    dpd: document.getElementById('form-dpd'),
    postnl: document.getElementById('form-postnl'),
    dhl: document.getElementById('form-dhl'),
  };
  carrierBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      carrierBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const carrier = btn.dataset.carrier;
      Object.entries(forms).forEach(([k, f]) => f.style.display = (k === carrier ? 'block' : 'none'));
      document.getElementById('po_result').classList.remove('show');
    });
  });

  // ---- conditional date fields ----
  // Onthulde velden worden ook verplicht: een "ja, ontvangen"-melding
  // zonder ontvangstdatum is voor de vervoerder onbruikbaar.
  function wireRadioReveal(groupSelector, valueThatShows, wrapId) {
    const wrap = document.getElementById(wrapId);
    const input = wrap.querySelector('input, textarea');
    root.querySelectorAll(groupSelector + ' input[type=radio]').forEach(r => {
      r.addEventListener('change', () => {
        root.querySelectorAll(groupSelector + ' .po-radio-opt').forEach(o => o.classList.remove('checked'));
        r.closest('.po-radio-opt').classList.add('checked');
        const show = (r.value === valueThatShows);
        wrap.style.display = show ? 'block' : 'none';
        if (input) input.required = show;
      });
    });
    root.querySelector(groupSelector + ' .po-radio-opt')?.classList.add('checked');
  }
  wireRadioReveal('#dpd_optie_group', 'ontvangen', 'dpd_datum_ontvangst_wrap');
  wireRadioReveal('#pn_optie_group', 'ontvangen', 'pn_datum_ontvangst_wrap');

  // Toelichting verplicht bij gedeeltelijke of beschadigde ontvangst
  const pnOpmerking = document.getElementById('pn_opmerking');
  const pnOptioneelLabel = document.getElementById('pn_opmerking_optional');
  root.querySelectorAll('#pn_optie_group input[type=radio]').forEach(r => {
    r.addEventListener('change', () => {
      const verplicht = (r.value === 'gedeeltelijk');
      pnOpmerking.required = verplicht;
      if (pnOptioneelLabel) pnOptioneelLabel.style.display = verplicht ? 'none' : '';
    });
  });

  function fmtDate(iso) {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}-${m}-${y}`;
  }

  // =====================================================
  // Handtekeningveld
  // Strokes worden genormaliseerd (0..1) bewaard, zodat het veld
  // schaalbaar blijft en de handtekening scherp naar PDF gaat.
  // =====================================================
  class SignaturePad {
    constructor(padRoot) {
      this.root = padRoot;
      this.canvas = padRoot.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.strokes = [];
      this.active = null;
      // Aspect-ratio van het veld op het moment van de eerste streek.
      // Strokes zijn per as genormaliseerd (0..1); zonder vaste referentie
      // rekt de inkt uit wanneer het veld van vorm verandert (toestel draaien).
      this.refAspect = null;
      this.onChange = () => {};
      this.resize();
      if (window.ResizeObserver) {
        new ResizeObserver(() => this.resize()).observe(this.canvas);
      } else {
        window.addEventListener('resize', () => this.resize());
      }
      this.bind();
    }

    cssSize() {
      const r = this.canvas.getBoundingClientRect();
      return { w: r.width, h: r.height };
    }

    resize() {
      const { w, h } = this.cssSize();
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const bw = Math.round(w * dpr), bh = Math.round(h * dpr);
      if (this.canvas.width === bw && this.canvas.height === bh) return;
      this.canvas.width = bw;
      this.canvas.height = bh;
      this.dpr = dpr;
      this.redraw();
    }

    // Rechthoek (css-px) waarbinnen de ref-ruimte ligt: zelfde aspect-ratio
    // als bij het tekenen, links/onder uitgelijnd (inkt hoort op de baseline).
    inkRect(w, h) {
      if (!this.refAspect) return { x: 0, y: 0, w, h };
      const cur = w / h;
      if (Math.abs(cur - this.refAspect) < 0.001) return { x: 0, y: 0, w, h };
      if (cur > this.refAspect) {
        return { x: 0, y: 0, w: h * this.refAspect, h };
      }
      const rh = w / this.refAspect;
      return { x: 0, y: h - rh, w, h: rh };
    }

    bind() {
      const c = this.canvas;
      const pos = (e) => {
        const r = c.getBoundingClientRect();
        const rect = this.inkRect(r.width, r.height);
        return {
          x: Math.min(1, Math.max(0, (e.clientX - r.left - rect.x) / rect.w)),
          y: Math.min(1, Math.max(0, (e.clientY - r.top - rect.y) / rect.h)),
          v: 0,
        };
      };
      c.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        c.setPointerCapture(e.pointerId);
        if (!this.strokes.length) {
          const r = c.getBoundingClientRect();
          this.refAspect = r.height ? r.width / r.height : null;
        }
        this.active = [pos(e)];
        this.strokes.push(this.active);
        this.redraw();
      });
      c.addEventListener('pointermove', (e) => {
        if (!this.active) return;
        e.preventDefault();
        const p = pos(e);
        const prev = this.active[this.active.length - 1];
        const d = Math.hypot(p.x - prev.x, p.y - prev.y);
        if (d < 0.002) return;
        p.v = prev.v * 0.6 + d * 0.4;
        this.active.push(p);
        this.redraw();
      });
      const end = () => {
        if (!this.active) return;
        this.active = null;
        this.changed();
      };
      c.addEventListener('pointerup', end);
      c.addEventListener('pointercancel', end);
      c.addEventListener('pointerleave', end);

      this.root.parentElement.querySelectorAll('.po-sig-tools button').forEach((b) => {
        b.addEventListener('click', () => {
          if (b.dataset.act === 'clear') this.clear();
          else this.undo();
        });
      });
    }

    strokeWidth(v, h) {
      const base = h / 150;
      return Math.max(1.2, Math.min(3.0, 3.0 - v * 90)) * base;
    }

    redraw() {
      const { w, h } = this.cssSize();
      const ctx = this.ctx;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#14181f';
      ctx.fillStyle = '#14181f';

      const rect = this.inkRect(w, h);
      for (const stroke of this.strokes) {
        const P = stroke.map((p) => ({ x: rect.x + p.x * rect.w, y: rect.y + p.y * rect.h, v: p.v }));
        if (P.length === 1) {
          ctx.beginPath();
          ctx.arc(P[0].x, P[0].y, this.strokeWidth(0, rect.h) / 1.6, 0, Math.PI * 2);
          ctx.fill();
          continue;
        }
        for (let i = 1; i < P.length; i++) {
          const a = P[i - 1], b = P[i];
          const from = i === 1 ? a : { x: (P[i - 2].x + a.x) / 2, y: (P[i - 2].y + a.y) / 2 };
          const to = i === P.length - 1 ? b : { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
          ctx.beginPath();
          ctx.lineWidth = this.strokeWidth(b.v, rect.h);
          ctx.moveTo(from.x, from.y);
          ctx.quadraticCurveTo(a.x, a.y, to.x, to.y);
          ctx.stroke();
        }
      }
      this.root.classList.toggle('has-ink', this.strokes.length > 0);
    }

    changed() {
      const tools = this.root.parentElement.querySelector('.po-sig-tools');
      const empty = this.isEmpty();
      tools.querySelectorAll('button').forEach((b) => (b.disabled = empty));
      const state = tools.querySelector('.po-sig-state');
      state.textContent = empty ? 'Nog niet ondertekend' : 'Ondertekend';
      state.classList.toggle('ok', !empty);
      if (!empty) this.root.classList.remove('invalid');
      this.onChange(empty);
    }

    undo() {
      this.strokes.pop();
      if (!this.strokes.length) this.refAspect = null;
      this.redraw();
      this.changed();
    }
    clear() {
      this.strokes = [];
      this.refAspect = null;
      this.redraw();
      this.changed();
    }
    isEmpty() { return this.strokes.length === 0; }

    // PNG met transparante achtergrond, strak rond de inkt gesneden zodat de
    // handtekening de ruimte op het formulier goed vult.
    toPng(pad = 10) {
      if (this.isEmpty()) return null;
      const W = this.canvas.width, H = this.canvas.height;
      const px = this.ctx.getImageData(0, 0, W, H).data;
      let minX = W, minY = H, maxX = -1, maxY = -1;
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          if (px[(y * W + x) * 4 + 3] > 8) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < 0) return null;
      minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
      maxX = Math.min(W - 1, maxX + pad); maxY = Math.min(H - 1, maxY + pad);
      const cw = maxX - minX + 1, ch = maxY - minY + 1;
      const out = document.createElement('canvas');
      out.width = cw; out.height = ch;
      out.getContext('2d').drawImage(this.canvas, minX, minY, cw, ch, 0, 0, cw, ch);
      return out.toDataURL('image/png');
    }
  }

  const sigPads = {
    dpd: new SignaturePad(document.getElementById('dpd_sigpad')),
    postnl: new SignaturePad(document.getElementById('pn_sigpad')),
    dhl_ontvanger: new SignaturePad(document.getElementById('dhl_sigpad_ontvanger')),
    dhl_afzender: new SignaturePad(document.getElementById('dhl_sigpad_afzender')),
  };

  function checkSignature(pad, errorId) {
    const err = document.getElementById(errorId);
    if (pad.isEmpty()) {
      pad.root.classList.add('invalid');
      err.classList.add('show');
      pad.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    err.classList.remove('show');
    return true;
  }

  function b64ToBytes(b64) {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }

  // Plaatst de handtekening in een vak (PDF-punten, oorsprong linksonder) en
  // behoudt daarbij de verhoudingen.
  async function placeSignature(pdfDoc, page, dataUrl, box) {
    const png = await pdfDoc.embedPng(b64ToBytes(dataUrl.split(',')[1]));
    const scale = Math.min(box.width / png.width, box.height / png.height);
    page.drawImage(png, {
      x: box.x,
      y: box.y,
      width: png.width * scale,
      height: png.height * scale,
    });
  }

  // Datum van ondertekening staat standaard op vandaag
  const vandaag = new Date().toISOString().slice(0, 10);
  ['dpd_datum_teken', 'pn_datum_teken'].forEach((id) => {
    const el = document.getElementById(id);
    if (el && !el.value) el.value = vandaag;
  });

  function showResult(pdfBytes, filename) {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    document.getElementById('po_pdf_preview').src = url;
    const dl = document.getElementById('po_download_link');
    dl.href = url;
    dl.download = filename;
    document.getElementById('po_result').classList.add('show');
    const step = document.getElementById('po_step_label');
    if (step) step.textContent = 'Stap 2 van 2';
    document.getElementById('po_result').scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // =====================================================
  // DPD: overlay op de originele template-PDF
  // =====================================================
  const PAGE_HEIGHT = 841.92;
  const ROWS = {
    verzenddatum:       { top: 268.34, bottom: 289.49 },
    pakketnummer:       { top: 289.49, bottom: 310.73 },
    naam_verzender:     { top: 310.73, bottom: 331.97 },
    naam_ontvanger:     { top: 331.97, bottom: 353.09 },
    email_ontvanger:    { top: 353.09, bottom: 374.33 },
    telefoon_ontvanger: { top: 374.33, bottom: 395.57 },
  };
  const VALUE_CELL_X = 302;
  function topToPdfY(topValue, offset = 6) { return PAGE_HEIGHT - topValue - offset; }

  // Onderstreepte regels in het ondertekeningsblok (gemeten in de template)
  const DPD_RULE_1_TOP = 707.35;   // datum van ondertekening | naam en functie
  const DPD_RULE_2_TOP = 749.65;   // bedrijfsnaam            | handtekening
  const DPD_RULE_1_Y = PAGE_HEIGHT - DPD_RULE_1_TOP + 3;
  const DPD_RULE_2_Y = PAGE_HEIGHT - DPD_RULE_2_TOP + 3;
  const DPD_DATE_RULE_Y = PAGE_HEIGHT - 496.5;   // stippellijn achter '(datum)'
  // Vrije ruimte tussen het label '(Signature)' en de regel eronder
  const DPD_SIG_BOX = {
    x: 305,
    y: PAGE_HEIGHT - DPD_RULE_2_TOP + 1.5,
    width: 200,
    height: (PAGE_HEIGHT - 729.5) - (PAGE_HEIGHT - DPD_RULE_2_TOP + 1.5),
  };

  forms.dpd.addEventListener('submit', async (e) => {
    e.preventDefault();
    const statusEl = document.getElementById('dpd_status');
    if (!checkSignature(sigPads.dpd, 'dpd_sig_error')) {
      statusEl.textContent = '';
      return;
    }
    statusEl.textContent = 'Bezig met genereren…';

    const optie = root.querySelector('input[name=dpd_optie]:checked').value;
    const data = {
      verzenddatum: fmtDate(document.getElementById('dpd_verzenddatum').value),
      pakketnummer: document.getElementById('dpd_pakketnummer').value,
      naam_verzender: document.getElementById('dpd_verzender').value,
      naam_ontvanger: document.getElementById('dpd_ontvanger').value,
      email_ontvanger: document.getElementById('dpd_email').value,
      telefoon_ontvanger: document.getElementById('dpd_telefoon').value,
      optie,
      datum_ontvangst: fmtDate(document.getElementById('dpd_datum_ontvangst').value),
      datum_ondertekening: fmtDate(document.getElementById('dpd_datum_teken').value),
      naam_ondertekenaar: document.getElementById('dpd_naam_teken').value,
      bedrijfsnaam: document.getElementById('dpd_bedrijfsnaam').value,
      handtekening: sigPads.dpd.toPng(),
    };

    try {
      const { PDFDocument, rgb, StandardFonts } = pdfLib();
      const pdfDoc = await PDFDocument.load(await fetchTemplate(TEMPLATE_URLS.dpd));
      const page = pdfDoc.getPages()[0];
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      for (const [field, { top }] of Object.entries(ROWS)) {
        const value = data[field];
        // baseline 12pt onder de cel-bovenrand = zelfde hoogte als het label
        if (value) page.drawText(String(value), { x: VALUE_CELL_X, y: topToPdfY(top, 12), size: 10, font, color: rgb(0, 0, 0) });
      }

      if (data.optie === 'ontvangen') {
        page.drawText('X', { x: 72.5, y: topToPdfY(444.2, 11), size: 11, font: fontBold });
        if (data.datum_ontvangst) page.drawText(data.datum_ontvangst, { x: 164, y: DPD_DATE_RULE_Y, size: 10, font });
      } else {
        page.drawText('X', { x: 72.5, y: topToPdfY(517.3, 11), size: 11, font: fontBold });
      }

      // Onderste blok: de waarden horen op de onderstreepte regels te staan.
      page.drawText(data.datum_ondertekening || '', { x: 76.2, y: DPD_RULE_1_Y, size: 10, font });
      page.drawText(data.naam_ondertekenaar || '', { x: 303.2, y: DPD_RULE_1_Y, size: 10, font });
      page.drawText(data.bedrijfsnaam || '', { x: 76.2, y: DPD_RULE_2_Y, size: 10, font });
      await placeSignature(pdfDoc, page, data.handtekening, DPD_SIG_BOX);

      const bytes = await pdfDoc.save();
      showResult(bytes, `dpd-verklaring-${data.pakketnummer || 'pakket'}.pdf`);
      statusEl.textContent = 'Klaar.';
    } catch (err) {
      console.error(err);
      statusEl.textContent = 'Er ging iets mis: ' + err.message;
    }
  });

  // =====================================================
  // PostNL: overlay op de officiële PostNL-PDF (2 pagina's)
  // =====================================================
  const PN_PAGE_HEIGHT = 842;
  function pnTopToPdfY(topValue, offset = 0) { return PN_PAGE_HEIGHT - topValue - offset; }
  function pnWhiteOut(page, top, bottom, x0, x1, pad = 2) {
    const { rgb } = pdfLib();
    page.drawRectangle({
      x: x0 - pad,
      y: PN_PAGE_HEIGHT - bottom - pad,
      width: (x1 - x0) + pad * 2,
      height: (bottom - top) + pad * 2,
      color: rgb(1, 1, 1),
    });
  }

  forms.postnl.addEventListener('submit', async (e) => {
    e.preventDefault();
    const statusEl = document.getElementById('pn_status');
    if (!checkSignature(sigPads.postnl, 'pn_sig_error')) {
      statusEl.textContent = '';
      return;
    }
    statusEl.textContent = 'Bezig met genereren…';

    const optie = root.querySelector('input[name=pn_optie]:checked').value;
    const data = {
      datum: fmtDate(new Date().toISOString().slice(0, 10)),
      barcode: document.getElementById('pn_barcode').value,
      optie,
      datum_ontvangst: fmtDate(document.getElementById('pn_datum_ontvangst').value),
      opmerking: document.getElementById('pn_opmerking').value,
      naam: document.getElementById('pn_naam').value,
      adres: document.getElementById('pn_adres').value,
      telefoon: document.getElementById('pn_telefoon').value,
      datum_teken: fmtDate(document.getElementById('pn_datum_teken').value),
      handtekening: sigPads.postnl.toPng(),
    };

    try {
      const { PDFDocument, rgb, StandardFonts } = pdfLib();
      const pdfDoc = await PDFDocument.load(await fetchTemplate(TEMPLATE_URLS.postnl));
      const pages = pdfDoc.getPages();
      const page1 = pages[0];
      const page2 = pages[1];
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // ---- PAGINA 1 ----
      pnWhiteOut(page1, 156.92, 167.53, 83.05, 114.71);
      page1.drawText(data.datum, { x: 83.05, y: pnTopToPdfY(167.53, -2), size: 10, font });

      pnWhiteOut(page1, 343.22, 353.83, 83.05, 260);
      page1.drawText(data.barcode, { x: 83.05, y: pnTopToPdfY(353.83, -2), size: 10, font: fontBold });

      pnWhiteOut(page1, 510.85, 530.35, 255.88, 400, 1);
      page1.drawText(data.barcode, { x: 255.88, y: pnTopToPdfY(530.35, 4), size: 13, font: fontBold });

      pnWhiteOut(page1, 596.42, 607.03, 355.83, 500, 1);
      page1.drawText(data.barcode, { x: 355.83, y: pnTopToPdfY(607.03, -2), size: 10, font: fontBold });

      const optionBoxes = {
        ontvangen: { top: 539.42, bottom: 550.03 },
        gedeeltelijk: { top: 562.22, bottom: 572.83 },
        niet_ontvangen: { top: 585.02, bottom: 595.63 },
      };
      const chosen = optionBoxes[data.optie];
      page1.drawText('X', { x: 82.5, y: pnTopToPdfY(chosen.bottom, 1), size: 11, font: fontBold });

      if (data.optie === 'ontvangen' && data.datum_ontvangst) {
        pnWhiteOut(page1, 539.42, 550.03, 196.51, 303.56, 1);
        page1.drawText(data.datum_ontvangst, { x: 198, y: pnTopToPdfY(550.03, -2), size: 10, font });
      }

      if (data.opmerking) {
        pnWhiteOut(page1, 642.02, 652.63, 83.05, 509.68, 1);
        pnWhiteOut(page1, 653.42, 664.03, 83.05, 300, 1);
        const words = data.opmerking.split(' ');
        let line1 = '', line2 = '';
        for (const w of words) {
          const test = line1 ? line1 + ' ' + w : w;
          if (font.widthOfTextAtSize(test, 10) <= 420 && !line2) line1 = test;
          else line2 = line2 ? line2 + ' ' + w : w;
        }
        page1.drawText(line1, { x: 83.05, y: pnTopToPdfY(652.63, -2), size: 10, font });
        if (line2) page1.drawText(line2, { x: 83.05, y: pnTopToPdfY(664.03, -2), size: 10, font });
      }

      pnWhiteOut(page1, 721.82, 732.43, 113.64, 434.80, 1);
      page1.drawText(data.naam, { x: 116, y: pnTopToPdfY(732.43, -2), size: 10, font });

      // ---- PAGINA 2 (Volgblad) ----
      pnWhiteOut(page2, 157.07, 167.68, 113.12, 412.66, 1);
      page2.drawText(data.adres, { x: 116, y: pnTopToPdfY(167.68, -2), size: 10, font });

      pnWhiteOut(page2, 179.87, 190.48, 159.02, 408.46, 1);
      page2.drawText(data.telefoon, { x: 161, y: pnTopToPdfY(190.48, -2), size: 10, font });

      pnWhiteOut(page2, 202.67, 213.28, 116.28, 339.88, 1);
      page2.drawText(data.datum_teken, { x: 119, y: pnTopToPdfY(213.28, -2), size: 10, font });

      // Handtekening: stippellijn wegwitten en de getekende handtekening erop zetten
      pnWhiteOut(page2, 236.87, 247.48, 146.90, 325.68, 1);
      const PN_SIG_BASE = PN_PAGE_HEIGHT - 247.48 + 1;
      await placeSignature(pdfDoc, page2, data.handtekening, {
        x: 149,
        y: PN_SIG_BASE,
        width: 175,
        height: (PN_PAGE_HEIGHT - 216) - PN_SIG_BASE,
      });

      const bytes = await pdfDoc.save();
      showResult(bytes, `postnl-verklaring-${data.barcode || 'zending'}.pdf`);
      statusEl.textContent = 'Klaar.';
    } catch (err) {
      console.error(err);
      statusEl.textContent = 'Er ging iets mis: ' + err.message;
    }
  });

  // =====================================================
  // DHL: heronderzoek / aanspraak maken op
  // =====================================================
  const DHL_PAGE_HEIGHT = 841.92;
  // De labels staan op size 9; de waarde krijgt dezelfde basislijn, zodat
  // label en waarde netjes op één lijn in het grijze vak staan.
  const dhlBaseline = (labelBottom) => DHL_PAGE_HEIGHT - labelBottom + 2.2;
  const DHL_VALUE_X = 175;
  const DHL_ROWS = {
    naam_afzender:      229.55,
    contact_afzender:   258.47,
    email_afzender:     286.70,
    adres_afzender:     314.90,
    naam_ontvanger:     377.66,
    contact_ontvanger:  407.30,
    email_ontvanger:    436.22,
    adres_ontvanger:    465.16,
    verzendnummer:      528.40,
    verzenddatum:       558.28,
    huidige_datum:      587.44,
  };
  // '[barcode hier invoegen]' in de verklaringstekst, inclusief gele markering
  const DHL_BARCODE = { top: 624.70, bottom: 635.62, x0: 424.0, x1: 514.0, baseline: dhlBaseline(635.47) };
  // De twee handtekeningregels onderaan
  const DHL_SIG_LINE_TOP = 742.22;
  const DHL_SIG_Y = DHL_PAGE_HEIGHT - DHL_SIG_LINE_TOP + 1.5;
  const DHL_SIG_ONTVANGER = { x: 73, y: DHL_SIG_Y, width: 158, height: 40 };
  const DHL_SIG_AFZENDER = { x: 285, y: DHL_SIG_Y, width: 158, height: 40 };
  // 'Naam:Naam:' staat in de template per ongeluk twee keer links onder elkaar
  const DHL_NAAM_ROW = { top: 760.75, bottom: 769.75, x0: 69.0, x1: 120.5, baseline: dhlBaseline(769.75) };

  // Verkleint de tekst tot hij binnen de beschikbare breedte past
  function fitFontSize(font, text, maxWidth, startSize) {
    let size = startSize;
    while (size > 5.5 && font.widthOfTextAtSize(text, size) > maxWidth) size -= 0.25;
    return size;
  }

  forms.dhl.addEventListener('submit', async (e) => {
    e.preventDefault();
    const statusEl = document.getElementById('dhl_status');
    if (!checkSignature(sigPads.dhl_ontvanger, 'dhl_sig_error')) {
      statusEl.textContent = '';
      return;
    }
    statusEl.textContent = 'Bezig met genereren…';

    const val = (id) => document.getElementById(id).value.trim();
    const data = {
      naam_afzender: val('dhl_naam_afzender'),
      contact_afzender: val('dhl_contact_afzender'),
      email_afzender: val('dhl_email_afzender'),
      adres_afzender: val('dhl_adres_afzender'),
      naam_ontvanger: val('dhl_naam_ontvanger'),
      contact_ontvanger: val('dhl_contact_ontvanger'),
      email_ontvanger: val('dhl_email_ontvanger'),
      adres_ontvanger: val('dhl_adres_ontvanger'),
      verzendnummer: val('dhl_verzendnummer'),
      verzenddatum: fmtDate(val('dhl_verzenddatum')),
      huidige_datum: fmtDate(new Date().toISOString().slice(0, 10)),
      hand_ontvanger: sigPads.dhl_ontvanger.toPng(),
      hand_afzender: sigPads.dhl_afzender.isEmpty() ? null : sigPads.dhl_afzender.toPng(),
    };

    try {
      const { PDFDocument, rgb, StandardFonts } = pdfLib();
      const pdfDoc = await PDFDocument.load(await fetchTemplate(TEMPLATE_URLS.dhl));
      const page = pdfDoc.getPages()[0];
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // De grijze invulvakken vullen
      for (const [field, labelBottom] of Object.entries(DHL_ROWS)) {
        if (!data[field]) continue;
        const size = fitFontSize(font, data[field], 340, 9.5);
        page.drawText(data[field], { x: DHL_VALUE_X, y: dhlBaseline(labelBottom), size, font });
      }

      // De placeholder '[barcode hier invoegen]' met gele markering wegwitten
      page.drawRectangle({
        x: DHL_BARCODE.x0,
        y: DHL_PAGE_HEIGHT - DHL_BARCODE.bottom - 0.6,
        width: DHL_BARCODE.x1 - DHL_BARCODE.x0,
        height: (DHL_BARCODE.bottom - DHL_BARCODE.top) + 1.2,
        color: rgb(1, 1, 1),
      });
      const bcSize = fitFontSize(fontBold, data.verzendnummer, 96, 9);
      page.drawText(data.verzendnummer, { x: 425.11, y: DHL_BARCODE.baseline, size: bcSize, font: fontBold });

      // Handtekeningen op hun eigen regel
      await placeSignature(pdfDoc, page, data.hand_ontvanger, DHL_SIG_ONTVANGER);
      if (data.hand_afzender) {
        await placeSignature(pdfDoc, page, data.hand_afzender, DHL_SIG_AFZENDER);
      }

      // In de template staat 'Naam:Naam:' links op elkaar gedrukt; wij zetten
      // elke naam onder de bijbehorende handtekening.
      page.drawRectangle({
        x: DHL_NAAM_ROW.x0,
        y: DHL_PAGE_HEIGHT - DHL_NAAM_ROW.bottom - 1,
        width: DHL_NAAM_ROW.x1 - DHL_NAAM_ROW.x0,
        height: (DHL_NAAM_ROW.bottom - DHL_NAAM_ROW.top) + 2,
        color: rgb(1, 1, 1),
      });
      page.drawText('Naam: ' + data.naam_ontvanger, { x: 70.82, y: DHL_NAAM_ROW.baseline, size: 9, font });
      page.drawText('Naam: ' + data.naam_afzender, { x: 283.25, y: DHL_NAAM_ROW.baseline, size: 9, font });

      const bytes = await pdfDoc.save();
      showResult(bytes, `dhl-heronderzoek-${data.verzendnummer || 'zending'}.pdf`);
      statusEl.textContent = 'Klaar.';
    } catch (err) {
      console.error(err);
      statusEl.textContent = 'Er ging iets mis: ' + err.message;
    }
  });
})();
